import type { View } from './types';
import { ArchiveView } from '../../components/archive';
import { PlayersView } from '../../components/players';
import { SeriespillHistoryModal } from '../../components/seriespill';
import { SingleMatchesView } from '../../components/singlematches';

export interface LibraryViewsProps {
  view: string;
  // archive branch
  archiveForArchive: any;
  effectivePlayerDB: any;
  goHome: () => void;
  openArchived: any;
  deleteArchived: any;
  importArchive: any;
  setProfilePlayerId: any;
  pausedTournaments: any;
  handleResumeTournament: any;
  handleDeletePaused: any;
  // NOTE: the monolith also passed onPauseRunning/onArchiveFromList/onDeleteAllPaused
  // to <ArchiveView>, but ArchiveView (extracted in PR-L) never declared/used them —
  // dead props, dropped here (behavior-identical; same class as the PR-T DocCard case).
  // seriespill history modal (rendered alongside the archive branch)
  serieHistoryFocus: any;
  activeSeries: any;
  setSerieHistoryFocus: any;
  // singleMatches branch
  singleMatches: any;
  plannedMatches: any;
  plannedMatchBookings: any;
  completePlannedMatch: any;
  cancelPlannedMatch: any;
  updatePlannedMatchPlayers: any;
  liveAllPlayers: any;
  exitSetupToActivities: any;
  setView: (v: View) => void;
  singleMatchesReturnView: View;
  addSingleMatch: any;
  updateSingleMatch: any;
  deleteSingleMatch: any;
  persistNewPlayerImmediate: any;
  Padel24Engine: any;
  // players branch (PlayersView, extracted + folded in PR-AJ). Uses archiveWithLive
  // (not archiveForArchive) and the player-CRUD mutators; forwards Padel24Engine +
  // the two monolith-only helpers down to the embedded PlayerHistoryModal.
  archiveWithLive: any;
  eloAdjustments: any;
  addPlayer: any;
  /** Puts a player found by exact name into this session's list (#1060). */
  adoptPlayer?: (player: any) => void;
  updatePlayer: any;
  deletePlayer: any;
  getPlayerTournamentHistory: any;
  calcPlayerPositionInTournament: any;
}

/**
 * Library / history view-dispatch group (AppShell step 4). Renders the archive
 * and single-matches views for the matching `view`; null otherwise. Branches
 * moved verbatim from the `Padel24AppMain` return JSX (legacy/App.tsx). The
 * `SeriespillHistoryModal` is rendered alongside the archive branch (guarded by
 * `serieHistoryFocus && activeSeries`, not by `view`), matching the monolith.
 * The `players` branch (PlayersView) was folded in here in PR-AJ.
 */
export function LibraryViews({
  view,
  archiveForArchive,
  effectivePlayerDB,
  goHome,
  openArchived,
  deleteArchived,
  importArchive,
  setProfilePlayerId,
  pausedTournaments,
  handleResumeTournament,
  handleDeletePaused,
  serieHistoryFocus,
  activeSeries,
  setSerieHistoryFocus,
  singleMatches,
  plannedMatches,
  plannedMatchBookings,
  completePlannedMatch,
  cancelPlannedMatch,
  updatePlannedMatchPlayers,
  liveAllPlayers,
  exitSetupToActivities,
  setView,
  singleMatchesReturnView,
  addSingleMatch,
  updateSingleMatch,
  deleteSingleMatch,
  persistNewPlayerImmediate,
  Padel24Engine,
  archiveWithLive,
  eloAdjustments,
  addPlayer,
  adoptPlayer,
  updatePlayer,
  deletePlayer,
  getPlayerTournamentHistory,
  calcPlayerPositionInTournament,
}: LibraryViewsProps) {
  return (
    <>
      {view === 'players' && (
        <PlayersView
          effectivePlayerDB={effectivePlayerDB}
          archive={archiveWithLive}
          singleMatches={singleMatches}
          eloAdjustments={eloAdjustments}
          onBack={goHome}
          onOpenArchived={openArchived}
          onAddCustomPlayer={addPlayer}
          onAdoptPlayer={adoptPlayer}
          onUpdateCustomPlayer={updatePlayer}
          onDeleteCustomPlayer={deletePlayer}
          Padel24Engine={Padel24Engine}
          getPlayerTournamentHistory={getPlayerTournamentHistory}
          calcPlayerPositionInTournament={calcPlayerPositionInTournament}
        />
      )}
      {view === 'archive' && (
        <ArchiveView
          archive={archiveForArchive}
          effectivePlayerDB={effectivePlayerDB}
          onBack={goHome}
          onOpenArchived={openArchived}
          onDeleteArchived={deleteArchived}
          onImport={importArchive}
          onPlayerClick={(p) => setProfilePlayerId(p.id)}
          pausedTournaments={pausedTournaments}
          onResumePaused={handleResumeTournament}
          onDeletePaused={handleDeletePaused}
        />
      )}
      {serieHistoryFocus && activeSeries && (
        <SeriespillHistoryModal
          classes={(activeSeries.config?.classes || []).filter(
            (c) => (c.seriesHistory || []).length > 0,
          )}
          initialSel={serieHistoryFocus}
          onClose={() => setSerieHistoryFocus(null)}
        />
      )}
      {view === 'singleMatches' && (
        <SingleMatchesView
          singleMatches={singleMatches}
          plannedMatches={plannedMatches}
          bookings={plannedMatchBookings}
          onCompletePlannedMatch={completePlannedMatch}
          onCancelPlannedMatch={cancelPlannedMatch}
          onUpdatePlannedMatchPlayers={updatePlannedMatchPlayers}
          allPlayers={liveAllPlayers}
          onBack={() => exitSetupToActivities(() => setView(singleMatchesReturnView))}
          onAddMatch={addSingleMatch}
          onUpdateMatch={updateSingleMatch}
          onDeleteMatch={deleteSingleMatch}
          onPlayerClick={(p) => setProfilePlayerId(p.id)}
          onPersistNewPlayer={persistNewPlayerImmediate}
          onOpenBanepad={() => setView('singleMatchesBanepad')}
          Padel24Engine={Padel24Engine}
        />
      )}
    </>
  );
}
